import React, {useEffect, useMemo} from 'react';
import { useSelector } from 'react-redux';
import {GreenroomState, GreenroomState as GS} from '../stores/types/types';
import { VID_TYPES } from '../types/constants';

/**
 * Created to preload the next video in the show, so that it is ready to play when the current shot ends.
 * We were running into an issue where bullman was delaying playing a video when switching shots, we are
 * hoping this will fix that issue.
 */
export default function PreloadVideo() {
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);

	const currentShot = useMemo(() => {
		if (currentShotId) {
			return shots.find(s => s.shot_id === currentShotId);
		}
		return shots?.[0];
	}, [currentShotId, shots]);

	const nextShot = useMemo(() => {
		if (!currentShot) return;

		const currentShotIndex = shots.findIndex(s => s.shot_id === currentShot?.shot_id);
		if (currentShotIndex >= 0 && currentShotIndex < shots.length - 1) {
			return shots[currentShotIndex + 1];
		} else if (currentShotIndex < 0) {
			return shots[0];
		}
		return shots?.[currentShotIndex];

	}, [currentShot, shots]);

	useEffect(() => {
		if (currentShot && nextShot && currentShot.shot_id !== nextShot.shot_id) {
			const shotMedia = nextShot.shot_data?.shotFeeds.filter(f => f.feedType === 'media');
			const firstShotFeedWithVideo = shotMedia?.find((sf) => media.find(
				m => m.id === sf.media && VID_TYPES.includes(m.type),
			));
			if (firstShotFeedWithVideo) {
				const mediaItem = media.find(m => m.id === firstShotFeedWithVideo.media);

				if (mediaItem?.images) {
					console.log(`Preloading video ${mediaItem?.images}`);

					const request = new XMLHttpRequest();
					request.open('GET', mediaItem.images, true);
					request.responseType = 'blob';
					request.onload = () => {
						if (request.status === 200) {
							const videoBlob = request.response;
							const videoUrl = URL.createObjectURL(videoBlob);
							const video = document.createElement('video');
							video.src = videoUrl;
							video.preload = 'auto';
							video.load();

							console.log(`Completed preloading video ${mediaItem?.images}`);
						}
					}
					request.onerror = () => {
						console.log(`Error pre loading video ${mediaItem?.images}`);
					}
					request.send();
				}
			}
		}
	}, [nextShot, currentShot, media]);

	return (<></>);
}